import cn from 'classnames';
import { CSSProperties, FC, HTMLProps, ReactNode, useEffect, useRef } from 'react';
import { useToggle } from '../../hooks/use-toggle';
import { Portal } from './portal/portal';
import { TooltipArrowIcon } from './tooltip-arrow-icon';
import s from './tooltip.module.css';

interface Props extends Omit<HTMLProps<HTMLUListElement>, 'onChange' | 'controls'> {
  wrapperClassName?: string;
  triggerWrapperClassName?: string;
  optionsWrapperClassName?: string;

  tabIndex?: number;
  closeOnContentClick?: boolean;
  trigger: ReactNode;
  children: ReactNode;

  withArrow?: boolean;
  disabled?: boolean;

  fullScreen?: boolean;

  open?: boolean;
  onOpen?: (state: boolean) => void;

  name?: string;

  behavior?: 'click' | 'hover';

  style?: CSSProperties;

  onClickTrigger?: (e: React.MouseEvent<HTMLDivElement>) => void;
  offset?: number;
  position?: 'top' | 'bottom' | 'left' | 'right';
  customTrigger?: HTMLElement;
  'data-testid'?: string;
  'data-name'?: string;
  'data-value'?: string;
}

export const Tooltip: FC<Props> = (props) => {
  const {
    children,
    optionsWrapperClassName,
    wrapperClassName,
    withArrow,
    triggerWrapperClassName,
    disabled,
    trigger,
    fullScreen,
    open,
    onOpen,
    tabIndex,
    behavior = 'click',
    closeOnContentClick,
    style,
    onClickTrigger,
    offset,
    position,
    customTrigger,
  } = props;

  const triggerRef = useRef<HTMLDivElement>(null);
  const [isOpen, toggleIsOpen, setIsOpen] = useToggle();

  useEffect(() => {
    if (open === undefined) return;
    setIsOpen(open);
  }, [open]);

  const handleHover = () => {
    if (behavior === 'hover') {
      onOpen?.(true);
      setIsOpen(true);
    }
  };

  const handleLeave = () => {
    if (behavior === 'hover') {
      onOpen?.(false);
      setIsOpen(false);
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (onClickTrigger) {
      onClickTrigger(e);
      e.stopPropagation();
    }

    if (behavior === 'click') {
      toggleIsOpen();
      onOpen?.(!isOpen);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    onOpen?.(false);
  };

  const componentClassname = cn(s.component, wrapperClassName);
  const triggerClassname = cn(
    s.trigger,
    s[behavior],
    triggerWrapperClassName,
    disabled && s.disabled,
  );
  const portalClassname = cn(s.list, s[behavior], optionsWrapperClassName);

  return (
    <div
      className={componentClassname}
      ref={triggerRef}
      onMouseEnter={handleHover}
      onMouseLeave={handleLeave}
      style={style}
      data-name={props['data-name']}
      data-value={props['data-value']}
    >
      {trigger && (
        <div
          className={triggerClassname}
          onClick={disabled ? undefined : handleClick}
          tabIndex={tabIndex || (disabled ? -1 : 0)}
          data-testid={props['data-testid']}
          role="button"
        >
          {trigger}
          {withArrow && <TooltipArrowIcon className={cn(s.arrow, isOpen && s.open)} />}
        </div>
      )}

      {isOpen && children && !disabled && (
        <Portal
          triggerRef={triggerRef}
          onClose={handleClose}
          className={portalClassname}
          fullScreen={fullScreen}
          behavior={behavior}
          closeOnContentClick={closeOnContentClick}
          offset={offset}
          position={position}
          customTrigger={customTrigger}
          isOpen={isOpen}
        >
          {children}
        </Portal>
      )}
    </div>
  );
};
